<script setup lang="ts">
import { Icon } from '@iconify/vue';
import { ThumbsUp } from 'lucide-vue-next';
import { onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
// import { fetchFavoriteExperimentsApi, toggleFavoriteApi } from '@/api/experiment/experimentApi.ts';
import { fetchExperimentTagsApi } from '@/api/experiment/experimentApi.ts';
import { fetchKnowledgePointsApi, fetchSubjectKnowledgePointsApi, fetchVisualizationApi, putVisualApi } from '@/api/resource/visualizations';

import { clearDynamicCourseContext, DYNAMIC_COURSE_RETURN_TO_KEY } from '@/components/course/courseUtils';
import AvatarButton from '@/components/navbar/AvatarButton.vue';

import { Badge } from '@/components/ui/badge';

import { Button } from '@/components/ui/button';
import { useConfigStore, useUserStore } from '@/store';
import { isVisitor } from '@/utils/auth.ts';
import { openImathbook } from '@/utils/imathbookSso';
// import { success } from '@/utils/toast.ts';

// const userStore = useUserStore();
const router = useRouter();
const configStore = useConfigStore();
const USER_GUIDE_URL = 'https://ecng9aaaudb2.feishu.cn/wiki/LKLiwqUBciCZK7kqwZrcEOn5nJf?from=from_copylink';

function openUserGuide() {
  window.open(USER_GUIDE_URL, '_blank', 'noopener,noreferrer');
}

const isFavorite = ref(false);

const title = ref<string>('');
const tags = ref<string[]>([]);

const route = useRoute();
let experimentTagRequestVersion = 0;
const EXPERIMENT_TAG_CACHE_PREFIX = 'experiment-tags:v2:';
const BUILTIN_KNOWLEDGE_POINT_TAGS: Record<string, string[]> = {
  'ball-battle': ['随机过程的分类', '马氏链的概念'],
  'conic-section': ['圆锥曲线'],
  'derivative-geometry': ['导数的几何意义（切线斜率）'],
  'derivative-basic': ['导数的定义', '求函数的导数', '导数运算法则的应用'],
  'discrete-distribution': ['离散型随机变量及其概率函数（分布律）', '分布函数的定义及性质'],
  'discrete-pmf': ['离散型随机变量及其概率函数（分布律）'],
  'double-integral': ['定义与可积性', '几何意义与物理意义', '计算'],
  'event-relationship': ['事件间的关系与运算'],
  'false-positive': ['条件概率的定义与性质', '贝叶斯公式'],
  'function-limit': ['函数极限的概念', '函数极限的性质', '函数极限的存在准则', '函数极限的运算'],
  'gabriel-horn': ['无穷区间上的反常积分', '无界函数的反常积分'],
  'height-stats': ['随机变量的期望', '方差的定义和运算性质'],
  'infinite-recurrence1': ['递推消元', '迭代'],
  'integrability-conditions': ['可积条件'],
  'lagrange-mvt': ['Lagrange中值定理'],
  'linear-combination': ['线性组合与线性表出'],
  'linear-equation': ['二元一次方程组有唯一解的充要条件'],
  'linear-equation-3': ['三个平面的位置关系'],
  'matrix-eigen': ['矩阵的特征值和特征向量', '求特征值和特征向量'],
  'mutually-exclusive-and-independent': ['事件间的关系与运算', '两事件相互独立'],
  'neighborhood': ['邻域'],
  'partial-derivative': ['概念与计算', '高阶偏导数'],
  'pdf-cdf': ['分布函数的定义及性质', '概率密度函数 p.d.f.'],
  'put-pin': ['概率与频率的区别'],
  'quadratic-function': ['二次函数的解析式', '二次函数定义域与值域的关系'],
  'sequence-limit': ['数列极限的概念', '数列极限的运算'],
  'simple-pendulum': ['类比三角函数研究简谐运动'],
  'space-curve': ['曲线弧长与曲率'],
  'spam-filtering': ['贝叶斯公式'],
  'spring-oscillator': ['类比三角函数研究简谐运动'],
  'taylor-expansion': ['带佩亚诺型余项的Taylor公式', '带拉格朗日型余项的Taylor公式'],
  'tilde': ['三角函数图像的变换'],
  'total-derivative': ['连续、可导与可微的关系', '一阶全微分的形式不变性'],
  'trig-overlay': ['函数图像重合', '三角函数图像的变换'],
  'trig-params': ['根据三角函数的对称性求参数'],
  'ferris-wheel': ['三角函数定义与圆周运动', '类比三角函数的定义研究圆周运动'],
};

function readCachedExperimentTags(expId: string) {
  try {
    const cached = JSON.parse(localStorage.getItem(`${EXPERIMENT_TAG_CACHE_PREFIX}${expId}`) || '[]');
    return Array.isArray(cached) ? cached.map(cleanKnowledgePointLabel).filter(Boolean) : [];
  }
  catch {
    return [];
  }
}

function cacheExperimentTags(expId: string, values: string[]) {
  if (!expId || values.length === 0)
    return;
  localStorage.setItem(`${EXPERIMENT_TAG_CACHE_PREFIX}${expId}`, JSON.stringify(values));
}

function cleanKnowledgePointLabel(value: unknown) {
  return String(value || '')
    .trim()
    .replace(/^\d+(?:\.\d+)+[.、，,:：\s-]*/, '')
    .trim();
}

function normalizeKnowledgePointLabel(value: unknown) {
  return cleanKnowledgePointLabel(value)
    .replaceAll('[', '')
    .replaceAll(']', '')
    .replace(/[的\s·•,，、。:：()（）【】_-]/g, '')
    .toLowerCase();
}

async function updateExperiment() {
  const requestVersion = ++experimentTagRequestVersion;
  const path = route.path.split('/').pop();
  tags.value = [];
  if (path === 'buffon') {
    title.value = '蒲丰投针';
    tags.value = ['几何概率', '微积分与积分计算', '大数定律', '蒙特卡罗模拟'];
  }
  else if (path === 'event-relationship') {
    title.value = '事件的关系与运算';
  }
  else if (path === 'discrete-pmf') {
    title.value = '离散型随机变量的PMF';
  }
  else if (path === 'pdf-cdf') {
    title.value = '概率密度函数与分布函数';
  }
  else if (path === 'mutually-exclusive-and-independent') {
    title.value = '互斥与独立';
  }
  else if (path === 'three-doors') {
    title.value = '三门问题';
    tags.value = ['条件概率', '乘法公式', '全概率公式', '贝叶斯公式', '蒙特卡罗模拟', '最优策略'];
  }
  else if (path === 'birthday-problem') {
    title.value = '生日问题';
    tags.value = ['排列组合', '大数定律', '蒙特卡罗模拟'];
  }
  else if (path === 'birthday-attack') {
    title.value = '生日攻击问题';
    tags.value = ['蒙特卡罗模拟', '生日问题', '条件概率', '泰勒展开', '独立事件的联合概率'];
  }
  else if (path === 'positive-test') {
    title.value = '阳性检测';
    tags.value = ['条件概率', '乘法公式', '全概率公式', '贝叶斯公式', '蒙特卡罗模拟'];
  }
  else if (path === 'false-positive') {
    title.value = '假阳性问题';
  }
  else if (path === 'spam-filtering') {
    title.value = '垃圾邮件过滤';
  }
  else if (path === 'binomial-distribution') {
    title.value = '二项分布';
    tags.value = ['X~B(n,p)'];
  }
  else if (path === 'poisson-distribution') {
    title.value = '泊松分布';
    tags.value = ['X~P(λ)'];
  }
  else if (path === 'geometric-distribution') {
    title.value = '几何分布';
    tags.value = ['X~GE(p)'];
  }
  else if (path === 'uniform-distribution') {
    title.value = '均匀分布';
    tags.value = ['X~U(a,b)'];
  }
  else if (path === 'exponential-distribution') {
    title.value = '指数分布';
    tags.value = ['X~E(λ)'];
  }
  else if (path === 'normal-distribution') {
    title.value = '正态分布';
    tags.value = ['X~N(μ,σ²)'];
  }
  else if (path === 'binomial-poisson') {
    title.value = '二项分布与泊松分布';
    tags.value = ['泊松近似定理 : np=λ，n很大，p很小，λ固定时近似'];
  }
  else if (path === 'binomial-normal') {
    title.value = '二项分布与正态分布';
    tags.value = ['np和np(1-p)足够大，即n→∞时近似'];
  }
  else if (path === 'poisson-exponential') {
    title.value = '泊松分布与指数分布';
    tags.value = ['泊松分布：事件的数量', '指数分布：事件之间的时间间隔', '公交车悖论'];
  }
  else if (path === 'poisson-normal') {
    title.value = '泊松分布与正态分布';
    tags.value = ['λ较大，泊松分布可近似为均值和方差均为λ的正态分布'];
  }
  else if (path === '2d-uniform-distribution') {
    title.value = '二维均匀分布';
    tags.value = ['联合分布', '边缘分布', '条件分布', '随机变量的独立性', '区域积分'];
  }
  else if (path === '2d-normal-distribution') {
    title.value = '二维正态分布';
    tags.value = ['联合分布', '边缘分布', '条件分布', '随机变量的独立性', '区域积分'];
  }
  else if (path === 'group-detection') {
    title.value = '分组检测';
    tags.value = ['事件的独立性', 'n重伯努利概型', '二项分布', '期望', '方差', '函数优化'];
  }
  else if (path === 'portfolio') {
    title.value = '投资组合优化';
    tags.value = ['期望', '方差', '协方差', '蒙特卡罗抽样'];
  }
  else if (path === 'NetworkTrafficCharacteristics') {
    title.value = '网络流量分析与异常检测系统';
    tags.value = ['相关系数矩阵', '蒙特卡罗模拟'];
  }
  else if (path === 'expectation-test') {
    title.value = '期望检验';
    tags.value = ['分组检测'];
  }
  else if (path === 'CoinFliping') {
    title.value = '抛硬币实验';
    tags.value = ['大数定律', '蒙特卡罗模拟'];
  }
  else if (path === 'EstimationOfPai') {
    title.value = '蒙特卡罗方法估算π值';
    tags.value = ['大数定律', '蒙特卡罗模拟'];
  }
  else if (path === 'central-limit-theorem') {
    title.value = '高尔顿钉板实验';
    tags.value = ['中心极限定理', '蒙特卡罗模拟'];
  }
  else if (path === 'bertrand') {
    title.value = '贝特朗悖论';
    tags.value = ['几何概率', '测度论', '概率的公理化定义'];
  }
  else if (path === 'distribution-clt') {
    title.value = '各种分布的中心极限定理';
    tags.value = ['分布的可加性'];
  }
  else if (path === 'brownian-motion') {
    title.value = '布朗运动模拟';
    tags.value = ['随机变量的独立性', '正态分布', '随机过程', '平稳过程', '独立增量过程'];
  }
  else if (path === 'geometric-brownian-motion') {
    title.value = '几何布朗运动模拟';
    tags.value = ['正态分布', '随机过程', '随机分布'];
  }
  else if (path === 'poisson-process') {
    title.value = '泊松过程模拟';
    tags.value = ['泊松分布', '随机过程', '平稳过程', '独立增量过程'];
  }
  else if (path === 'ball-battle') {
    title.value = '红蓝圆竞争模型';
  }
  else if (path === 't-distribution') {
    title.value = 't分布';
    tags.value = ['正态分布', '卡方分布', '抽样分布'];
  }
  else if (path === 'chi-square-distribution') {
    title.value = '卡方分布';
    tags.value = ['正态分布', '随机变量和的分布', '抽样分布'];
  }
  else if (path === 't-distribution-quantile') {
    title.value = 't分布上分位点';
    tags.value = ['t分布'];
  }
  else if (path === 'chi-square-distribution-quantile') {
    title.value = '卡方分布上分位点';
    tags.value = ['卡方分布'];
  }
  else if (path === 'sequence-limit') {
    title.value = '数列的极限';
  }
  else if (path === 'function-limit') {
    title.value = '函数的极限';
  }
  else if (path === 'derivative-geometry') {
    title.value = '导数的几何意义';
  }
  else if (path === 'lagrange-mvt') {
    title.value = '拉格朗日中值定理';
  }
  else if (path === 'taylor-expansion') {
    title.value = '泰勒展开';
  }
  else if (path === 'integrability-conditions') {
    title.value = '可积条件';
  }
  else if (path === 'gabriel-horn') {
    title.value = '加百利喇叭';
  }
  else if (path === 'neighborhood') {
    title.value = '邻域';
  }
  else if (path === 'partial-derivative') {
    title.value = '偏导数';
  }
  else if (path === 'total-derivative') {
    title.value = '全微分';
  }
  else if (path === 'linear-equation') {
    title.value = '二元一次方程组';
  }
  else if (path === 'linear-equation-3') {
    title.value = '三元一次方程组';
  }
  else if (path === 'height-stats') {
    title.value = '身高数据分析';
  }
  else if (path === 'double-integral') {
    title.value = '二重积分';
  }
  else if (path === 'space-curve') {
    title.value = '空间曲线弧长';
  }
  else if (path === 'linear-combination') {
    title.value = '二维平面向量的线性组合';
  }
  else if (path === 'matrix-eigen') {
    title.value = '二阶矩阵的特征值与特征向量';
  }
  else if (path === 'discrete-distribution') {
    title.value = '离散均匀分布的PMF与CDF随n增大向连续型的演化';
  }
  else if (path === 'quadratic-function') {
    title.value = '二次函数可视化';
  }
  else if (path === 'conic-section') {
    title.value = '圆锥曲线辅助画图工具';
  }
  else if (path === 'derivative-basic') {
    title.value = '导数辅助工具';
  }
  else if (path === 'tilde') {
    title.value = '波浪线';
  }
  else if (path === 'put-pin') {
    title.value = '抛图钉问题';
  }
  else if (path === 'infinite-recurrence1') {
    title.value = '无限递归数列1';
  }
  else if (path === 'trig-params') {
    title.value = '三角函数含参问题';
  }
  else if (path === 'ferris-wheel') {
    title.value = '摩天轮形成三角函数图像';
  }
  else if (path === 'spring-oscillator') {
    title.value = '弹簧振子形成三角函数图像';
  }
  else if (path === 'simple-pendulum') {
    title.value = '单摆形成三角函数图像';
  }
  else if (path === 'trig-overlay') {
    title.value = '三角函数叠加';
  }
  else {
    title.value = configStore.currentExperimentOwnerName || (useConfigStore().currentSubject?.name ?? '邮趣数学');
    tags.value = [];
  }

  if (tags.value.length === 0 && path) {
    const builtinTags = BUILTIN_KNOWLEDGE_POINT_TAGS[path] ?? [];
    tags.value = builtinTags.length > 0
      ? [...builtinTags]
      : readCachedExperimentTags(path);
  }

  // 已有标签以当前前端配置为准；仅为空时才使用上传实验时勾选的知识点。
  if (isInExperimentPage() && tags.value.length === 0) {
    try {
      const experimentTags = await fetchExperimentTagsApi(path || '');
      if (requestVersion === experimentTagRequestVersion && experimentTags.length > 0) {
        tags.value = Array.from(new Set(
          experimentTags
            .map(tag => String(tag.name || '').trim())
            .map(cleanKnowledgePointLabel)
            .filter(Boolean),
        ));
        cacheExperimentTags(path || '', tags.value);
      }
    }
    catch {
      // Some dynamic visualization routes do not use the route suffix as exp-id.
    }

    if (requestVersion === experimentTagRequestVersion && tags.value.length === 0) {
      const visualizationName = configStore.currentExperimentOwnerName || title.value;
      if (visualizationName) {
        try {
          const resources = await fetchVisualizationApi(visualizationName);
          if (requestVersion === experimentTagRequestVersion) {
            const resource = resources.find(item => String(item.name || '').trim() === visualizationName.trim());
            const selectedKnowledgePoints = resource?.knowledge_points ?? [];
            const chineseNameById = new Map<string, string>();
            if (resource?.chapter?.id) {
              try {
                const response: any = await fetchKnowledgePointsApi(resource.chapter.id);
                const chapterKnowledgePoints = Array.isArray(response)
                  ? response
                  : (response?.knowledgepoints ?? response?.results ?? []);
                chapterKnowledgePoints.forEach((point: any) => {
                  chineseNameById.set(String(point.id), String(point.name || '').trim());
                });
              }
              catch (exception) {
                console.error('Failed to map visualization knowledge-point names:', exception);
              }
            }
            if (requestVersion !== experimentTagRequestVersion)
              return;
            tags.value = Array.from(new Set(
              selectedKnowledgePoints
                .map(point => chineseNameById.get(String(point.id)) || String(point?.name || '').trim())
                .map(cleanKnowledgePointLabel)
                .filter(Boolean),
            ));
            cacheExperimentTags(path || '', tags.value);
          }
        }
        catch (exception) {
          console.error('Failed to load visualization knowledge-point tags:', exception);
        }
      }
    }

    // Legacy built-in experiments may have an Experiment record but no linked
    // VisualizationResource. In that case, match against the current subject's
    // Chinese knowledge-point list instead of displaying an unrelated resource.
    if (requestVersion === experimentTagRequestVersion && tags.value.length === 0) {
      const subjectId = configStore.currentSubject?.id_;
      const normalizedTitle = normalizeKnowledgePointLabel(title.value);
      if (subjectId && normalizedTitle) {
        try {
          const response: any = await fetchSubjectKnowledgePointsApi(subjectId);
          const subjectKnowledgePoints = Array.isArray(response)
            ? response
            : (response?.knowledgepoints ?? response?.results ?? []);
          const candidates = subjectKnowledgePoints
            .map((point: any) => ({
              label: cleanKnowledgePointLabel(point?.name),
              normalized: normalizeKnowledgePointLabel(point?.name),
            }))
            .filter((point: { label: string; normalized: string }) => point.label && point.normalized.startsWith(normalizedTitle));
          const exactMatches = candidates.filter((point: { normalized: string }) => point.normalized === normalizedTitle);
          const matched = exactMatches.length > 0 ? exactMatches : candidates;
          if (requestVersion === experimentTagRequestVersion) {
            tags.value = Array.from(new Set(matched.map((point: { label: string }) => point.label)));
            cacheExperimentTags(path || '', tags.value);
          }
        }
        catch (exception) {
          console.error('Failed to match subject knowledge-point tags:', exception);
        }
      }
    }
  }
  if (!isVisitor()) {
    const userStore = useUserStore();
    isFavorite.value = userStore.favoriteExperiments.map(item => item.expId).includes(path ?? '');
  }

  // Update liked status based on current page
  const cfg = configStore;
  if (isInExperimentPage() && cfg.currentExperimentOwnerName) {
    const key = `liked:${cfg.currentExperimentOwnerName}`;
    cfg.currentExperimentLiked = Boolean(localStorage.getItem(key));
  }
  else {
    cfg.currentExperimentLiked = false;
  }
}

onMounted(() => {
  updateExperiment();
  // if (!isVisitor()) {
  //   getMessage();
  // }
});

watch(() => route.path, () => {
  updateExperiment();
});

watch(() => configStore.currentExperimentOwnerName, () => {
  if (isInExperimentPage())
    updateExperiment();
});

// const messageList = ref<Message[]>([]);
// const messageNumber = ref(0);
//
// async function getMessage() {
//   try {
//     const result = await fetchMessagesApi();
//     messageNumber.value = result.messages.length;
//     console.log(result.messages);
//
//     for (let i = 0; i < result.messages.length; i++) {
//       if (!result.messages[i].read) {
//         messageList.value.push(result.messages[i]);
//         console.log(result.messages[i]);
//       }
//       else {
//         messageList.value.push(result.messages[i]);
//         messageNumber.value -= 1;
//       }
//     }
//     console.log('消息', messageList.value);
//   }
//   catch (error) {
//     console.error('Error during fetching messages:', error);
//   }
// }

// async function readMessage() {
//   try {
//     await readMessagesApi();
//     messageNumber.value = 0;
//   }
//   catch (error) {
//     console.error('Error during read messages: ', error);
//   }
// }

function toggleLike() {
  const cfg = configStore;
  if (!cfg.currentExperimentLikeEnabled || !cfg.currentExperimentOwnerName)
    return;

  const key = `liked:${cfg.currentExperimentOwnerName}`;
  const currentlyLiked = Boolean(localStorage.getItem(key));
  cfg.currentExperimentLiked = !currentlyLiked;

  if (cfg.currentExperimentLikeCount === null || cfg.currentExperimentLikeCount === undefined)
    cfg.currentExperimentLikeCount = 0;

  if (!currentlyLiked) {
    cfg.currentExperimentLikeCount = Number(cfg.currentExperimentLikeCount) + 1;
    localStorage.setItem(key, '1');
  }
  else {
    cfg.currentExperimentLikeCount = Math.max(0, Number(cfg.currentExperimentLikeCount) - 1);
    localStorage.removeItem(key);
  }

  // persist to backend if we have an id
  if (cfg.currentExperimentLikeId) {
    try {
      // putVisualApi expects (id, likes)
      putVisualApi(Number(cfg.currentExperimentLikeId), Number(cfg.currentExperimentLikeCount)).catch((e) => {
        console.warn('putVisualApi failed', e);
      });
    }
    catch (e) {
      console.error('Error during putVisualApi:', e);
    }
  }
}

function isInExperimentPage() {
  return route.path.startsWith('/experiment/');
}

function leaveCurrentSubject() {
  if (configStore.dynamicSubject) {
    if (route.path !== '/') {
      router.push('/');
      return;
    }
    const returnTo = sessionStorage.getItem(DYNAMIC_COURSE_RETURN_TO_KEY)
      || (typeof route.query.returnTo === 'string' ? route.query.returnTo : '');
    configStore.dynamicSubject = null;
    clearDynamicCourseContext();
    router.push(returnTo || '/');
    return;
  }

  if (configStore.currentSubject && route.path !== '/') {
    router.push('/');
    return;
  }

  configStore.currentSubjectId = null;
  router.push('/');
}
</script>

<template>
  <nav class="z-50 flex h-12 w-full items-center gap-2 rounded-xl border bg-background px-2 shadow-sm">
    <div class="flex items-center justify-center gap-2.5 overflow-x-hidden">
      <Button
        v-auto-animate
        variant="ghost"
        class="flex h-10 items-center px-2"
        @click="leaveCurrentSubject"
      >
        <Icon v-if="configStore.currentSubject" icon="lucide:chevron-left" class="size-6 text-gray-500 mr-1" />
        <div class="mr-1.5 flex size-7 items-center justify-center rounded-full bg-primary p-0 text-primary-foreground">
          <component :is="configStore.currentSubject.icon" v-if="configStore.currentSubject" class="size-6" />
          <img v-else src="/logo-math.svg" alt="Logo" class="size-7 rounded-full">
        </div>
        <div class="text-base text-primary font-bold shrink-0 mr-1.5">
          {{ isInExperimentPage() && title ? title : (useConfigStore().currentSubject?.name ?? '邮趣数学') }}
        </div>
      </Button>
      <div class="flex gap-2 overflow-x-auto">
        <Badge v-for="item in tags" :key="item" class="text-sm shrink-0">
          {{ item }}
        </Badge>
      </div>
      <div class="flex items-center gap-2">
        <Button
          v-if="configStore.currentExperimentLikeEnabled && !isVisitor()"
          size="icon" variant="ghost" class="p-1 size-auto"
          @click="toggleLike"
        >
          <ThumbsUp
            class="size-5 transition-all" :style="{
              fill: configStore.currentExperimentLiked ? '#FF5A5F' : 'none',
              stroke: configStore.currentExperimentLiked ? '#FF5A5F' : '#999',
            }"
          />
        </Button>
        <div v-if="configStore.currentExperimentLikeEnabled" class="text-sm text-muted-foreground mr-2">
          {{ configStore.currentExperimentLikeCount ?? 0 }}
        </div>
      </div>
    </div>
    <div class="ml-auto flex items-center gap-1.5 rounded-xl bg-slate-50/80 p-1 ring-1 ring-slate-200/70">
      <Button
        aria-label="大模型回答"
        title="大模型回答"
        :variant="route.path === '/ai' ? 'secondary' : 'ghost'"
        class="h-8 gap-2 rounded-lg px-3 text-sm font-medium"
        @click="router.push('/ai')"
      >
        <Icon icon="lucide:sparkles" class="size-4 text-primary" />
        <span class="hidden lg:inline">大模型回答</span>
      </Button>
      <Button
        aria-label="试卷分析"
        title="试卷分析"
        :variant="route.path === '/exam-analysis' ? 'secondary' : 'ghost'"
        class="h-8 gap-2 rounded-lg px-3 text-sm font-medium"
        @click="router.push('/exam-analysis')"
      >
        <Icon icon="lucide:file-search-2" class="size-4 text-primary" />
        <span class="hidden lg:inline">试卷分析</span>
      </Button>
      <Button
        aria-label="iMathBook"
        title="iMathBook"
        variant="ghost"
        class="h-8 gap-2 rounded-lg px-3 text-sm font-medium"
        @click="openImathbook"
      >
        <Icon icon="lucide:book-open" class="size-4 text-primary" />
        <span class="hidden lg:inline">iMathBook</span>
      </Button>
      <Button
        aria-label="用户手册"
        title="用户手册"
        variant="ghost"
        class="h-8 gap-2 rounded-lg px-3 text-sm font-medium"
        @click="openUserGuide"
      >
        <Icon icon="lucide:book-open-text" class="size-4 text-primary" />
        <span class="hidden lg:inline">用户手册</span>
      </Button>
    </div>
    <div class="flex items-center gap-4">
      <!--      <div v-if="!isVisitor()" class="relative flex items-center justify-center ml-auto"> -->
      <!--        <Popover> -->
      <!--          <PopoverTrigger> -->
      <!--            <Button size="icon" class="size-8 relative text-muted-foreground" variant="ghost"> -->
      <!--              <Bell :stroke-width="2" class="size-6" /> -->
      <!--              <Badge -->
      <!--                v-if="messageNumber !== 0" -->
      <!--                class="absolute right-1.5 top-0 translate-x-1/2 rounded-full min-w-4 h-4 p-0 flex items-center justify-center" -->
      <!--                variant="destructive" -->
      <!--              > -->
      <!--                {{ messageNumber <= 99 ? messageNumber : '99+' }} -->
      <!--              </Badge> -->
      <!--            </Button> -->
      <!--          </PopoverTrigger> -->
      <!--          <PopoverContent class="w-96 p-0"> -->
      <!--            <div class="flex items-center px-4 py-3"> -->
      <!--              <Label class="font-bold text-base"> 我的消息 </Label> -->
      <!--              <button -->
      <!--                class="ml-auto text-xs text-blue-600 hover:underline disabled:opacity-50" -->
      <!--                :disabled="!messageList.length || messageNumber === 0" -->
      <!--                @click="readMessage()" -->
      <!--              > -->
      <!--                全部已读 -->
      <!--              </button> -->
      <!--            </div> -->
      <!--            <Separator /> -->
      <!--            <div -->
      <!--              class="max-h-96 overflow-y-auto custom-scrollbar px-2 py-2 flex flex-col gap-1" -->
      <!--              :class="{ 'items-center justify-center': !messageList.length }" -->
      <!--            > -->
      <!--              <div -->
      <!--                v-if="!messageList.length" -->
      <!--                class="text-sm text-muted-foreground py-10 select-none" -->
      <!--              > -->
      <!--                暂无消息 -->
      <!--              </div> -->

      <!--              <div -->
      <!--                v-for="item in messageList" -->
      <!--                :key="item.messageId" -->
      <!--                class="group flex gap-3 rounded-md px-3 py-2 relative hover:bg-muted cursor-pointer transition" -->
      <!--                :class="{ 'opacity-70': item.read }" -->
      <!--                @click=" -->
      <!--                  item.type === 'pin' -->
      <!--                    ? router.push(`/experiment/${item.pinData?.comment.expId}`) -->
      <!--                    : item.type === 'reply' -->
      <!--                      ? router.push(`/experiment/${item.replyData?.comment.expId}`) -->
      <!--                      : item.type === 'like' -->
      <!--                        ? router.push(`/experiment/${item.likeData?.comment.expId}`) -->
      <!--                        : null -->
      <!--                " -->
      <!--              > -->
      <!--                <Avatar class="shrink-0 mt-0.5"> -->
      <!--                  <template v-if="item.type === 'post'"> -->
      <!--                    <AvatarImage :src="item.postData?.post.author.avatarUrl || ''" /> -->
      <!--                    <AvatarFallback>{{ item.postData?.post.author.realName }}</AvatarFallback> -->
      <!--                  </template> -->
      <!--                  <template v-else-if="item.type === 'pin'"> -->
      <!--                    <AvatarImage :src="item.pinData?.user.avatarUrl || ''" /> -->
      <!--                    <AvatarFallback>{{ item.pinData?.user.realName }}</AvatarFallback> -->
      <!--                  </template> -->
      <!--                  <template v-else-if="item.type === 'reply'"> -->
      <!--                    <AvatarImage :src="item.replyData?.reply.user.avatarUrl || ''" /> -->
      <!--                    <AvatarFallback>{{ item.replyData?.reply.user.realName }}</AvatarFallback> -->
      <!--                  </template> -->
      <!--                  <template v-else-if="item.type === 'like'"> -->
      <!--                    <AvatarImage :src="item.likeData?.user.avatarUrl || ''" /> -->
      <!--                    <AvatarFallback>{{ item.likeData?.user.realName }}</AvatarFallback> -->
      <!--                  </template> -->
      <!--                  <template v-else-if="item.type === 'delete'"> -->
      <!--                    <AvatarImage :src="item.likeData?.user?.avatarUrl || ''" /> -->
      <!--                    <AvatarFallback>管理员</AvatarFallback> -->
      <!--                  </template> -->
      <!--                </Avatar> -->

      <!--                <div class="flex flex-col min-w-0"> -->
      <!--                  <span class="text-sm font-medium truncate"> -->
      <!--                    <template v-if="item.type === 'post'">{{ item.postData?.post.author.realName }}</template> -->
      <!--                    <template v-else-if="item.type === 'pin'">{{ item.pinData?.user.realName }}</template> -->
      <!--                    <template v-else-if="item.type === 'reply'">{{ item.replyData?.reply.user.realName }}</template> -->
      <!--                    <template v-else-if="item.type === 'like'">{{ item.likeData?.user.realName }}</template> -->
      <!--                    <template v-else-if="item.type === 'delete'">管理员</template> -->
      <!--                  </span> -->
      <!--                  <span -->
      <!--                    class="text-xs text-muted-foreground leading-snug mt-0.5 line-clamp-2" -->
      <!--                  > -->
      <!--                    <template v-if="item.type === 'post'">老师发布了新的班级公告</template> -->
      <!--                    <template v-else-if="item.type === 'pin'">老师置顶了你的评论</template> -->
      <!--                    <template v-else-if="item.type === 'reply'">回复了你的评论</template> -->
      <!--                    <template v-else-if="item.type === 'like'">赞了你的评论</template> -->
      <!--                    <template v-else-if="item.type === 'delete'">管理员删除了你的评论</template> -->
      <!--                  </span> -->
      <!--                </div> -->

      <!--                <span -->
      <!--                  v-if="!item.read" -->
      <!--                  class="absolute right-2 top-2 inline-block w-2 h-2 rounded-full bg-red-500" -->
      <!--                /> -->
      <!--              </div> -->
      <!--            </div> -->
      <!--          </PopoverContent> -->
      <!--        </Popover> -->
      <!--      </div> -->
      <AvatarButton v-if="!isVisitor()" />
      <Button v-else @click="router.push('/login')">
        登录
      </Button>
    </div>
  </nav>
</template>

<style scoped></style>
